'use client';

import { useCallback } from 'react';
import { apiFetch } from '@/lib/api/apiClient';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';

const refreshAuth = async (): Promise<boolean> => {
    try {
        const auth = await apiFetch(`/auth/is_authenticated`, { method: 'GET' });
        return !!auth.is_authenticated_server;
    } catch {
        return false;
    }
};

export function useBoardEditorSaveFile() {
    const { appendLog, appendErrorLog } = useBoardEditorStore();

    const save = useCallback(async () => {
        const { currentFileId, getCurrentFile, prepareSave, updateFile, setCurrentFile } =
            useBoardEditorFileStore.getState();
        const { flashSaved } = useBoardEditorStore.getState();

        const currentFile = getCurrentFile();
        if (!currentFileId || !currentFile?.isDirty) return;
        if (!(await refreshAuth())) return;

        try {
            const prepared = prepareSave(currentFileId);
            if (!prepared) return;

            updateFile(currentFileId, { isSaving: true });

            if (prepared.isNewFile) {
                const res = await apiFetch('/boards/edit/code_files', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        code_content: prepared.codeContent,
                        block_content: prepared.blockContent,
                        file_name: prepared.name,
                    }),
                });
                const resourceId = String(res.resource_id);
                updateFile(currentFileId, {
                    id: resourceId,
                    isNewFile: false,
                    isSaving: false,
                    isSaved: true,
                    lastSavedCodeContent: prepared.codeContent,
                    lastSavedBlockContent: prepared.blockContent,
                    lastModified: new Date().toISOString(),
                });
                setCurrentFile(resourceId);
                appendLog(`🔅『${prepared.name}』を保存しました。`);
                flashSaved(resourceId);
            } else {
                await apiFetch('/boards/edit/code_files', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        resource_id: Number(currentFileId),
                        code_content: prepared.codeContent,
                        block_content: prepared.blockContent,
                        file_name: prepared.name,
                    }),
                });
                updateFile(currentFileId, {
                    isSaving: false,
                    isSaved: true,
                    lastSavedCodeContent: prepared.codeContent,
                    lastSavedBlockContent: prepared.blockContent,
                    lastModified: new Date().toISOString(),
                });
                flashSaved(currentFileId);
                appendLog(`🔅『${prepared.name}』を保存しました。`);
            }
        } catch (error: unknown) {
            updateFile(currentFileId, { isSaving: false });
            appendErrorLog(error);
        }
    }, [appendLog, appendErrorLog]);

    return { save };
}
